import { useIntl } from 'react-intl';

import Spacings from '@dynatrace/strato-design-tokens/spacings';
import { ZoomInIcon, ZoomOutIcon } from '@dynatrace/strato-icons';

import { allowWhenKeyIsEnter } from './utils.js';
import { useGridPlotSize } from '../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { ChartToolbarRenderer } from '../../../core/components/toolbar/ChartToolbarRenderer.js';
import { useTriggerZoom } from '../../../core/components/zoom/hooks/useTriggerZoom.js';
import { useA11yToolbarTabIndex } from '../../../core/hooks/useA11yToolbarTabIndex.js';
import { ZOOM_ACTIONS, ZOOM_CHANGE_TYPE } from '../../../core/types/zoom.js';
import { getPrimaryModifierKey } from '../../../core/utils/get-primary-modifier-key.js';
import { useXYChartData } from '../../hooks/useXYChartData.js';
import { XYCHART_MESSAGES } from '../../messages/messages.js';
import { useZoomState } from '../../store/selectors.js';
import { useApplyZoomDelta } from '../overlay/hooks/useApplyZoomDelta.js';

export const ZoomInOutButtons = () => {
  const tabIndexIn = useA11yToolbarTabIndex(ZOOM_ACTIONS.ZoomIn);
  const tabIndexOut = useA11yToolbarTabIndex(ZOOM_ACTIONS.ZoomOut);
  const { zoomInEnabled: zoomInState, zoomOutEnabled: zoomOutState } =
    useZoomState();
  const { axesMetadata } = useXYChartData();

  const [zoomInEnabled, zoomOutEnabled] = axesMetadata.isZoomEnabled
    ? [zoomInState, zoomOutState]
    : [false, false];

  const intl = useIntl();

  const { width } = useGridPlotSize();
  const applyZoom = useApplyZoomDelta();
  const triggerZoom = useTriggerZoom(width, applyZoom);

  const zoomInAction = () => {
    triggerZoom(ZOOM_CHANGE_TYPE.ZoomIn);
  };
  const zoomOutAction = () => {
    triggerZoom(ZOOM_CHANGE_TYPE.ZoomOut);
  };

  const zoomInShortcut = `${getPrimaryModifierKey()}+${intl.formatMessage(
    XYCHART_MESSAGES.zoomActionArrowUp,
  )}`;
  const zoomOutShortcut = `${getPrimaryModifierKey()}+${intl.formatMessage(
    XYCHART_MESSAGES.zoomActionArrowDown,
  )}`;

  const controlStyle = { marginRight: Spacings.Size2 };

  return (
    <>
      <ChartToolbarRenderer.Control
        tooltipText={intl.formatMessage(XYCHART_MESSAGES.zoomIn)}
        prefixIcon={<ZoomInIcon />}
        tooltipKeyShortcut={zoomInShortcut}
        onKeyDown={allowWhenKeyIsEnter(zoomInAction)}
        onClick={zoomInAction}
        tabIndex={tabIndexIn}
        disabled={!zoomInEnabled}
        divider="left"
        style={controlStyle}
        data-dt-name="ToolbarZoomIn"
      />
      <ChartToolbarRenderer.Control
        prefixIcon={<ZoomOutIcon />}
        tooltipText={intl.formatMessage(XYCHART_MESSAGES.zoomOut)}
        tooltipKeyShortcut={zoomOutShortcut}
        onKeyDown={allowWhenKeyIsEnter(zoomOutAction)}
        onClick={zoomOutAction}
        tabIndex={tabIndexOut}
        disabled={!zoomOutEnabled}
        style={controlStyle}
        data-dt-name="ToolbarZoomOut"
      />
    </>
  );
};

ZoomInOutButtons['displayName'] = 'ZoomInOutButtons';
